// Phase 40 Plan 02 Task 4 — first-sign-in upload + multi-device routing.
// CONTEXT D-02, D-15, D-16. RESEARCH.md Pattern 5.
// Phase 40 Plan 03 Task 1 — adds drainWal + scheduleWalDrain (D-13/D-14, RESEARCH §6).
//
// Public exports:
//   firstSignInUpload({ jwt, plants, areas, completions, notes, inventory, openConflictModal })
//     → { mode, garden_id?, message?, plants?, areas?, completions?, notes?, inventory? }
//   uploadToCloud({ jwt, plants, areas, completions, notes, inventory })
//     → { ok, garden_id?, row_counts?, message? }
//   downloadFromCloud({ jwt })
//     → { plants, areas, completions, notes, inventory, garden_id }
//
// Routes called: GET /auth/me · GET /sync/peek · POST /sync/initial-upload
// Worker base URL resolves from import.meta.env.VITE_WORKER_URL with a
// production default. UK English in any user-visible error strings (D-16).
//
// Plan 03 wires the real MultiDeviceConflictModal handler via openConflictModal.
// Plan 02 ships with the default 'cloud' resolver so the chain doesn't break.

const DEFAULT_WORKER_URL = 'https://tend-api.envision8.com';

export function getApiBase() {
  try {
    return (import.meta.env && import.meta.env.VITE_WORKER_URL) || DEFAULT_WORKER_URL;
  } catch {
    return DEFAULT_WORKER_URL;
  }
}

function authHeaders(jwt, extra = {}) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${jwt}`,
    ...extra,
  };
}

async function jsonOrThrow(res) {
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = j?.error || j?.detail || '';
    } catch {
      detail = `HTTP ${res.status}`;
    }
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return res.json();
}

// Boot the user record + default garden + owner membership (D-06). Returns
// { user_id, gardens: [...] } shaped from workers/account.js handleAuthMe.
async function authMe(jwt) {
  const res = await fetch(`${getApiBase()}/auth/me`, {
    method: 'GET',
    headers: authHeaders(jwt),
  });
  return jsonOrThrow(res);
}

// Cloud-side row counts for the user's primary garden — used to detect the
// silent-upload / silent-download / conflict tri-state (D-15).
async function syncPeek(jwt) {
  const res = await fetch(`${getApiBase()}/sync/peek`, {
    method: 'GET',
    headers: authHeaders(jwt),
  });
  return jsonOrThrow(res);
}

/**
 * Upload localStorage to the cloud. Atomic: success returns the cloud's
 * row_counts; the caller MUST verify counts match BEFORE rewriting
 * localStorage (D-02). Failure leaves localStorage untouched.
 */
export async function uploadToCloud({ jwt, plants, areas, completions, notes, inventory }) {
  // D-16 verbatim error copy is locked — return the friendly user-facing
  // message on failure regardless of the server's internal error string,
  // and capture the server detail in `detail` for diagnostics/Sentry.
  try {
    const res = await fetch(`${getApiBase()}/sync/initial-upload`, {
      method: 'POST',
      headers: authHeaders(jwt),
      body: JSON.stringify({ plants, areas, completions, notes, inventory }),
    });
    if (!res.ok) {
      let detail = '';
      try { const j = await res.json(); detail = j?.error || j?.detail || ''; } catch {}
      return {
        ok: false,
        message: "Couldn't save to cloud, your data is safe locally.",
        detail: detail || `HTTP ${res.status}`,
      };
    }
    const data = await res.json();
    return { ok: true, garden_id: data.garden_id, row_counts: data.row_counts };
  } catch (e) {
    return {
      ok: false,
      message: "Couldn't save to cloud, your data is safe locally.",
      detail: e?.message || 'network error',
    };
  }
}

/**
 * Read the canonical garden state back from the cloud. Plan 02 uses the
 * Worker /sync/peek for counts and the Supabase JS SDK directly for table
 * reads (RLS scopes to the user's gardens via JWT). Plan 03 may add a
 * dedicated /sync/full endpoint.
 *
 * For Plan 02 we read via the Supabase REST API with the user's JWT —
 * keeps the contract simple and avoids depending on a /sync/full route.
 */
export async function downloadFromCloud({ jwt }) {
  // Resolve the user's primary garden via /auth/me.
  const me = await authMe(jwt);
  const gardenId = me?.gardens?.[0]?.id;
  if (!gardenId) {
    // Empty cloud — return empty shape.
    return { plants: [], areas: [], completions: {}, notes: {}, inventory: {}, garden_id: null };
  }

  // Worker base URL drives the Supabase URL via VITE_SUPABASE_URL — Worker
  // doesn't proxy table reads, so we hit Supabase directly with the user's
  // JWT. RLS membership-join policy scopes the rows to the user's gardens.
  const supabaseUrl = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SUPABASE_URL) || '';
  const anonKey = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SUPABASE_ANON_KEY) || '';
  if (!supabaseUrl) {
    // Without a configured Supabase URL we fall back to empty cloud state.
    return { plants: [], areas: [], completions: {}, notes: {}, inventory: {}, garden_id: gardenId };
  }

  const fetchTable = async (table) => {
    const res = await fetch(`${supabaseUrl}/rest/v1/${table}?garden_id=eq.${gardenId}&select=*`, {
      headers: {
        apikey: anonKey,
        Authorization: `Bearer ${jwt}`,
        Accept: 'application/json',
      },
    });
    if (!res.ok) return [];
    return res.json();
  };

  const [plantsRows, areasRows, completionRows, dismissalRows, noteRows, inventoryRows] = await Promise.all([
    fetchTable('plants'),
    fetchTable('areas'),
    fetchTable('completions'),
    fetchTable('dismissals'),
    fetchTable('notes'),
    fetchTable('inventory'),
  ]);

  // Reshape Supabase rows back into the localStorage-canonical shape.
  // Plants: schema stores name/variety/latin/etc. with locations/tasks/colour
  // as JSONB. Local id is `local_id`. Map back to the local-shape `id` field.
  const plants = plantsRows.map((r) => ({
    id: r.local_id,
    cloudId: r.id,
    name: r.name,
    variety: r.variety,
    latin: r.latin,
    type: r.type,
    count: r.count,
    wiki: r.wiki,
    height: r.height,
    spread: r.spread,
    addedVia: r.added_via,
    prospectId: r.prospect_id,
    locations: r.locations || [],
    tasks: r.tasks || {},
    colour: r.colour || {},
  }));

  const areas = areasRows.map((r) => ({
    id: r.local_id,
    cloudId: r.id,
    name: r.name,
    subAreas: r.sub_areas || [],
  }));

  // Completions/dismissals: localStorage stores as { 'done:1:prune:4:2026': true }
  // — we rebuild that from the rows by key prefix.
  const completions = {};
  for (const c of completionRows) completions[c.key] = true;
  for (const d of dismissalRows) completions[d.key] = true;

  const notes = {};
  for (const n of noteRows) {
    const k = String(n.plant_id);
    if (!notes[k]) notes[k] = [];
    notes[k].push(n.body);
  }

  const inventory = {};
  for (const i of inventoryRows) {
    inventory[i.product_id] = { stock: i.stock_level };
  }

  return {
    plants,
    areas,
    completions,
    notes,
    inventory,
    garden_id: gardenId,
  };
}

/**
 * Orchestrate the first-sign-in upload — D-15 routing:
 *   - both empty                          → noop
 *   - local has data, cloud empty         → silent upload
 *   - local empty, cloud has data         → silent download
 *   - both have data                      → openConflictModal('cloud' or 'local')
 *
 * Returns a structured result the caller (AuthProvider) feeds back into
 * localStorage + UI state. Failed uploads return { mode: 'failed', message }
 * with localStorage untouched (D-02).
 */
export async function firstSignInUpload({
  jwt,
  plants = [],
  areas = [],
  completions = {},
  notes = {},
  inventory = {},
  openConflictModal = () => Promise.resolve('cloud'),
}) {
  // Boot the cloud-side user + default garden first (D-06). If this fails
  // we treat the whole flow as failed — without a garden id nothing can
  // succeed downstream.
  try {
    await authMe(jwt);
  } catch (e) {
    return { mode: 'failed', message: "Couldn't save to cloud, your data is safe locally." };
  }

  // Peek the cloud state to detect the tri-state.
  let peek;
  try {
    peek = await syncPeek(jwt);
  } catch {
    // Peek failure → treat as failed; localStorage stays intact.
    return { mode: 'failed', message: "Couldn't save to cloud, your data is safe locally." };
  }

  const cloudHasData = !!(peek?.row_counts && (peek.row_counts.plants > 0 || peek.row_counts.areas > 0));
  const localHasData = (plants.length > 0) || (areas.length > 0);

  if (!localHasData && !cloudHasData) {
    return { mode: 'noop' };
  }

  if (localHasData && !cloudHasData) {
    // Silent upload path.
    const up = await uploadToCloud({ jwt, plants, areas, completions, notes, inventory });
    if (!up.ok) {
      return { mode: 'failed', message: up.message || "Couldn't save to cloud, your data is safe locally." };
    }
    // D-02 — verify row_counts before declaring success.
    const rc = up.row_counts || {};
    if (rc.plants !== plants.length) {
      return { mode: 'failed', message: "Couldn't save to cloud, your data is safe locally." };
    }
    // Read canonical state back so cloudId is populated locally.
    let snap;
    try {
      snap = await downloadFromCloud({ jwt });
    } catch {
      // Upload succeeded — counts verified. Return what we sent up; the
      // caller can re-fetch later.
      return { mode: 'uploaded', garden_id: up.garden_id, plants, areas, completions, notes, inventory };
    }
    return { mode: 'uploaded', garden_id: up.garden_id, ...snap };
  }

  if (!localHasData && cloudHasData) {
    // Silent download path.
    let snap;
    try {
      snap = await downloadFromCloud({ jwt });
    } catch {
      return { mode: 'failed', message: "Couldn't load your cloud garden, please try again." };
    }
    return { mode: 'downloaded', ...snap };
  }

  // Both have data — open the conflict modal. Plan 02 default returns
  // 'cloud' so silent download is the safe fallback.
  let choice;
  try {
    choice = await openConflictModal({
      local: { plants: plants.length, areas: areas.length },
      cloud: peek.row_counts,
    });
  } catch {
    choice = 'cloud';
  }

  if (choice === 'local') {
    const up = await uploadToCloud({ jwt, plants, areas, completions, notes, inventory });
    if (!up.ok) {
      return { mode: 'failed', message: up.message || "Couldn't save to cloud, your data is safe locally." };
    }
    let snap;
    try { snap = await downloadFromCloud({ jwt }); } catch { snap = { plants, areas, completions, notes, inventory }; }
    return { mode: 'conflict-resolved-local', garden_id: up.garden_id, ...snap };
  }

  // Default 'cloud' — silent download replaces local.
  let snap;
  try {
    snap = await downloadFromCloud({ jwt });
  } catch {
    return { mode: 'failed', message: "Couldn't load your cloud garden, please try again." };
  }
  return { mode: 'conflict-resolved-cloud', ...snap };
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 40 Plan 03 Task 1 — WAL drain (CONTEXT D-13/D-14, RESEARCH §6).
// ─────────────────────────────────────────────────────────────────────────────
// drainWal — POST batches of WAL ops to /sync; cloud-wins on last_modified.
// scheduleWalDrain — registers an `online` listener + 30s polling drain.
// stopWalDrain — clears the polling interval (called on sign-out).
//
// Cross-tab serialisation via navigator.locks.request('tend-sync', { ifAvailable: true })
// (Pitfall 6) — exactly one tab drains at a time; concurrent invocations bail
// with `{ skipped: 'lock-busy' }`. Falls back to non-locked drain on browsers
// without the Web Locks API (e.g. Safari < 15.4).
//
// Retry schedule (D-14): exponential 1s / 4s / 16s. After 3 failed attempts
// the op moves to `tend:wal-deadletter` and surfaces in DeadLetterModal.

import { readWal, writeWal, pushDeadLetter, popWalBatch } from './storage.js';

const BATCH_SIZE = 50;
const RETRY_BACKOFF_MS = [1000, 4000, 16000];

export async function drainWal({ jwt, onStatusChange } = {}) {
  if (!jwt) return { drained: 0, deadLettered: 0, skipped: 'no-jwt' };
  const hasLocks = typeof navigator !== 'undefined' && navigator?.locks?.request;
  if (!hasLocks) return drainWalUnlocked({ jwt, onStatusChange });
  return navigator.locks.request('tend-sync', { ifAvailable: true }, async (lock) => {
    if (!lock) return { drained: 0, deadLettered: 0, skipped: 'lock-busy' };
    return drainWalUnlocked({ jwt, onStatusChange });
  });
}

async function drainWalUnlocked({ jwt, onStatusChange }) {
  let drained = 0;
  let deadLettered = 0;
  while (true) {
    const wal = readWal();
    if (wal.length === 0) break;
    const batch = popWalBatch(BATCH_SIZE);
    if (typeof onStatusChange === 'function') {
      onStatusChange({ status: 'syncing', count: wal.length });
    }

    let res = null;
    try {
      res = await fetch(`${getApiBase()}/sync`, {
        method: 'POST',
        headers: authHeaders(jwt),
        body: JSON.stringify({ ops: batch }),
      });
    } catch {
      res = null;
    }

    if (!res || !res.ok) {
      // Increment attempts on the batch ops; dead-letter when ≥3.
      const updated = readWal();
      const batchKeys = new Set(batch.map((o) => `${o.ts}|${o.cloudId}|${o.op}`));
      const next = updated.map((op) => {
        const key = `${op.ts}|${op.cloudId}|${op.op}`;
        return batchKeys.has(key) ? { ...op, attempts: (op.attempts || 0) + 1 } : op;
      });
      const survivors = [];
      for (const op of next) {
        const key = `${op.ts}|${op.cloudId}|${op.op}`;
        if (op.attempts >= 3 && batchKeys.has(key)) {
          pushDeadLetter(op, res ? `HTTP ${res.status}` : 'network_error');
          deadLettered += 1;
        } else {
          survivors.push(op);
        }
      }
      writeWal(survivors);
      // Backoff before retrying the next loop iteration. Use the prior
      // attempt count of the batch's first op as the backoff index.
      const attempt = (batch[0]?.attempts ?? 0);
      await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS[Math.min(attempt, 2)]));
      continue;
    }

    let json = {};
    try { json = await res.json(); } catch {}
    drained += json.applied || 0;
    // Cloud-wins rejected ops are silently dropped — local state no longer
    // matters because the cloud's version is newer. Drop the entire batch.
    const remaining = readWal().slice(batch.length);
    writeWal(remaining);
  }

  if (typeof onStatusChange === 'function') {
    onStatusChange({
      status: deadLettered > 0 ? 'failed' : 'synced',
      count: deadLettered,
    });
  }
  return { drained, deadLettered };
}

let drainInterval = null;
export function scheduleWalDrain({ jwt, onStatusChange } = {}) {
  if (drainInterval || typeof window === 'undefined') return;
  const trigger = () => {
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      drainWal({ jwt, onStatusChange });
    } else if (typeof onStatusChange === 'function') {
      onStatusChange({ status: 'offline', count: 0 });
    }
  };
  window.addEventListener('online', trigger);
  drainInterval = setInterval(trigger, 30_000);
  trigger(); // immediate kick
  // Hold a reference so stopWalDrain can detach the listener too.
  scheduleWalDrain._trigger = trigger;
}

export function stopWalDrain() {
  if (drainInterval) {
    clearInterval(drainInterval);
    drainInterval = null;
  }
  if (scheduleWalDrain._trigger && typeof window !== 'undefined') {
    window.removeEventListener('online', scheduleWalDrain._trigger);
    scheduleWalDrain._trigger = null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 40 Plan 04 Task 2 — fetchActivityLog (GET /auth/me/activity-log).
// ─────────────────────────────────────────────────────────────────────────────
// Wraps the paginated GET endpoint with the standard Authorization header.
// Returns { entries, has_more } on success; throws on non-2xx.
//
//   fetchActivityLog({ jwt })                       — first page
//   fetchActivityLog({ jwt, before: createdAt })    — older entries

export async function fetchActivityLog({ jwt, before, limit } = {}) {
  if (!jwt) throw new Error('jwt required');
  const url = new URL(`${getApiBase()}/auth/me/activity-log`);
  if (before) url.searchParams.set('before', before);
  if (limit)  url.searchParams.set('limit', String(limit));
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: authHeaders(jwt),
  });
  return jsonOrThrow(res);
}
